/**
 * USDC Permit signing helpers
 *
 * EIP-2612 permit for direct USDC approvals (alternative to Permit2).
 */
import { type Address, erc20Abi, type Hex, type WalletClient } from "viem"
import { readContract } from "viem/actions"
import { permitAbi } from "../../abi/permit"
import type { PermitSignature, SigningPayload } from "../../types"
import type { ReadableClient } from "../contracts"

// =============================================================================
// Constants
// =============================================================================

/** USDC EIP-712 domain version (same on Base mainnet and testnet) */
export const USDC_PERMIT_VERSION = "2"

// =============================================================================
// Types
// =============================================================================

export const PERMIT_TYPES = {
	Permit: [
		{ name: "owner", type: "address" },
		{ name: "spender", type: "address" },
		{ name: "value", type: "uint256" },
		{ name: "nonce", type: "uint256" },
		{ name: "deadline", type: "uint256" },
	],
} as const

// =============================================================================
// Helpers
// =============================================================================

/**
 * Get the permit nonce for a user
 */
export async function getPermitNonce(
	client: ReadableClient,
	usdc: Address,
	owner: Address,
): Promise<bigint> {
	return readContract(client, {
		address: usdc,
		abi: permitAbi,
		functionName: "nonces",
		args: [owner],
	})
}

/**
 * Get token name for EIP-712 domain
 */
export async function getTokenName(client: ReadableClient, token: Address): Promise<string> {
	return readContract(client, {
		address: token,
		abi: erc20Abi,
		functionName: "name",
	})
}

/**
 * Build EIP-2612 permit signing payload
 */
export function buildPermitSigningPayload(
	chainId: number,
	tokenAddress: Address,
	tokenName: string,
	owner: Address,
	spender: Address,
	value: bigint,
	nonce: bigint,
	deadline: bigint,
): SigningPayload {
	return {
		domain: {
			name: tokenName,
			version: USDC_PERMIT_VERSION,
			chainId,
			verifyingContract: tokenAddress,
		},
		types: PERMIT_TYPES,
		primaryType: "Permit",
		message: {
			owner,
			spender,
			value,
			nonce,
			deadline,
		},
	}
}

// =============================================================================
// Permit Signing
// =============================================================================

export interface SignPermitParams {
	walletClient: WalletClient
	tokenAddress: Address
	tokenName: string
	tokenVersion?: string
	permit: {
		owner: Address
		spender: Address
		value: bigint
		nonce: bigint
		deadline: bigint
	}
}

/**
 * Sign an EIP-2612 permit for USDC
 */
export async function signPermit(params: SignPermitParams): Promise<PermitSignature> {
	const { walletClient, tokenAddress, tokenName, tokenVersion, permit } = params

	const chainId = await walletClient.getChainId()
	const payload = buildPermitSigningPayload(
		chainId,
		tokenAddress,
		tokenName,
		permit.owner,
		permit.spender,
		permit.value,
		permit.nonce,
		permit.deadline,
	)

	// Use custom version if provided
	if (tokenVersion) {
		payload.domain.version = tokenVersion
	}

	const signature = await walletClient.signTypedData({
		account: permit.owner,
		domain: payload.domain as Parameters<typeof walletClient.signTypedData>[0]["domain"],
		types: payload.types,
		primaryType: payload.primaryType,
		message: payload.message,
	})

	// Parse signature into v, r, s components
	const r = `0x${signature.slice(2, 66)}` as Hex
	const s = `0x${signature.slice(66, 130)}` as Hex
	const v = Number.parseInt(signature.slice(130, 132), 16)

	return { v, r, s }
}
